import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, Typography, Grid, Button, TextField, InputAdornment , IconButton, Stack, ThemeProvider, FormControlLabel, Checkbox, Divider, Hidden } from '@mui/material';
import config from 'src/config';
import Iconify from 'src/components/iconify';
import { useNavigate } from 'react-router-dom';
import { getToken, login, storeKey, storeToken } from 'src/services/auth.service';
import { createTheme } from '@mui/material/styles'
import { AuthHeader } from 'src/components/auth/header';
import { AuthFooter } from 'src/components/auth/footer';
import SSOLogin from 'src/components/auth/sso';
import authMethods from 'src/methods/auth.methods';

const theme = createTheme({
    palette: {
      primary: {
        main: '#ff9933'
      }
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: '8px',
                    boxShadow: 'none',
                    '&:hover': {
                        boxShadow: 'none',
                    },
                    textTransform: 'none'
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    borderRadius: '8px',
                    '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                            borderRadius: '8px',
                        },
                    },
                },
            },
        },
    }
});

class LoginPage extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            show_password: false,

            pristine: {
                username: true,
                password: true,
                confirm_password: true
            },

            // payload
            username: '',
            password: '',
            ip_details: null,
        }

    }

    async componentDidMount() {
        let token = await getToken();
        if (token) {
            this.props.navigate('/files');
            return;
        }

        let ip_details = await authMethods.getDetailsFromIp();
        if (ip_details) {
            this.setState({ ip_details: ip_details });
        }
    }


    validateUsername(username, checkPristine = true) {

        if (checkPristine && this.state.pristine.username) {
            return true;
        }

        if (!username) {
            return false;
        }

        if (username.indexOf('@') === -1) {
            return false;
        }

        if (username.indexOf('.') === -1) {
            return false;
        }

        return true;
    }

    validatePassword(password, checkPristine = true) {

        if (checkPristine && this.state.pristine.password) {
            return true;
        }

        if (!password) {
            return false;
        }

        if (password.length < 8) {
            return false;
        }

        return true;
    }

    validateForm() {
        return this.validateUsername(this.state.username, false) && this.validatePassword(this.state.password, false);
    }


    async handleLogin() {
        let payload = {
            username: this.state.username,
            password: this.state.password,
            ip_details: this.state.ip_details
        };

        let loginRes = await login(payload);
        if (loginRes.error) {
            console.log(loginRes.error);
            alert('Error: ' + loginRes.error);
        }

        if (loginRes.token) {
            console.log('Login successful, token:', loginRes.token);
            await storeToken(loginRes.token);
            await storeKey('username', loginRes?.data?.username);
            if (loginRes?.data?.role) {
                await storeKey('role', loginRes?.data?.role);
            } else {
                await storeKey('role', 'user');
            }

            // Redirect to verification page
            this.props.navigate('/verify');

            // this.props.navigate('/files');
        }
    }

    render() {

        return (
            <ThemeProvider theme={theme}>
                <Helmet>
                    <title> Login | {config.APP_NAME} </title>
                </Helmet>

                <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: '100vh', p: 2 }}>

                    <Grid item xs={12} md={6} sx={{ justifyContent: 'center', alignItems: 'center', display: 'flex' }}>

                        <Grid item xs={12} sm={11} md={10} lg={9} xl={8}>


                            <AuthHeader />

                            <Typography sx={{ fontWeight: 'bold', mt: 5 }}>
                                Email address
                            </Typography>
                            <TextField
                                fullWidth
                                name="email"
                                size='small'
                                sx={{ mb: 2 }}
                                value={this.state.username}
                                onChange={(e) => {
                                    this.setState({
                                        username: e.target.value,
                                        pristine: {
                                            ...this.state.pristine,
                                            username: false
                                        }
                                    });
                                }}
                                placeholder='you@example.com'
                                error={!this.validateUsername(this.state.username)}
                                helperText={!this.validateUsername(this.state.username) ? 'Invalid email address' : ''}
                            />

                            <Stack direction="row" justifyContent="space-between">
                                <Typography sx={{ fontWeight: 'bold' }}>
                                    Password
                                </Typography>
                                <Link
                                    variant="subtitle2"
                                    underline="hover"
                                    sx={{ cursor: 'pointer', color: 'gray' }}
                                    onClick={() => {
                                        this.props.navigate('/forgotpassword');
                                    }}
                                >
                                    Forgot my password
                                </Link>
                            </Stack>
                            <TextField
                                fullWidth
                                name="password"
                                size='small'
                                placeholder='••••••••'
                                type={this.state.show_password ? 'text' : 'password'}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => {
                                                    this.setState({ show_password: !this.state.show_password });
                                                }}
                                                edge="end"
                                            >
                                                <Iconify icon={this.state.show_password ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                                value={this.state.password}
                                onChange={(e) => {
                                    this.setState({
                                        password: e.target.value,
                                        pristine: {
                                            ...this.state.pristine,
                                            password: false
                                        }
                                    });
                                }}
                                error={!this.validatePassword(this.state.password)}
                                helperText={!this.validatePassword(this.state.password) ? 'Password must be at least 8 characters' : ''}
                            />

                            <Stack direction="row" sx={{ mb: 2 }} justifyContent={'space-between'}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={this.state.rememberMe}
                                            onChange={(e) => {
                                                this.setState({ rememberMe: e.target.checked });
                                            }}
                                            sx={{ color: 'gray' }}
                                        />
                                    }
                                    label="Remember me"
                                    labelPlacement='start'
                                    sx={{ justifyContent: 'flex-start', m: 0, color: 'gray' }}
                                />

                                <Link
                                    variant="subtitle2"
                                    underline="hover"
                                    sx={{ cursor: 'pointer', mt: '9px' }}
                                    onClick={() => {
                                        this.props.navigate('/registertype');
                                    }}
                                >
                                    New User?
                                </Link>
                            </Stack>

                            <Button
                                fullWidth
                                // size="small"
                                type="submit"
                                variant="contained"
                                onClick={this.handleLogin.bind(this)}
                                disabled={!this.validateForm()}
                                style={{ color: 'white' }}
                            >
                                Sign in
                            </Button>

                            {/* Horizontal line with text inbetween */}
                            <Divider sx={{ my: 2 }}>
                                <Typography sx={{ color: 'gray' }}>
                                    or
                                </Typography>
                            </Divider>

                            <SSOLogin />

                            <AuthFooter />

                        </Grid>

                    </Grid>

                    <Grid item md={6} sx={{ display: { xs: 'none', sm: 'none', md: 'flex' }, justifyContent: 'center', alignItems: 'center' }}>
                        <Grid item xs={12}>
                            <Typography variant="h6" sx={{ textAlign: 'center', fontSize: 50 }}>
                                <span>More&nbsp;clarity. </span>
                                <span style={{ color: 'gray' }}>Less&nbsp;chaos.</span>
                            </Typography>
                        </Grid>
                    </Grid>
                </Grid>
            </ThemeProvider>
        )
    }
}

export default function (props) {
    const navigate = useNavigate();

    return <LoginPage {...props} navigate={navigate} />;
}

